// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\doctors\ArchivedDoctorsPage\index.js

import React, { useEffect, useState, useCallback } from 'react';
import { 
  Table, Button, Spin, Alert, Tooltip, Card, Row, Col, 
  Tag, Modal, Select, Input, Typography, Space 
} from 'antd';
import { toast } from 'react-toastify';
import { UndoOutlined, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getArchivedPersons, restorePerson } from '../../../redux/slices/persons/personsThunk';
import axiosService from '../../../services/axiosServicesVisit';
import { API_URL, API } from '../../../config/api';

const { Text } = Typography;

// Construction de l'URL de base Persons
const apiUrlBase = `${API_URL}${API.API_PREFIX || '/api'}${API.VERSION || '/v1'}`;
const persons_api_base_url = `${apiUrlBase}${API.PERSONS?.BASE || "/persons"}`;

const ArchivedDoctorsPage = () => {
  const dispatch = useDispatch();
  const { archivedPersons, loading, error } = useSelector((state) => state.persons);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10 });

  // --- ÉTATS ÉDITION MOTIF ---
  const [isReasonModalVisible, setIsReasonModalVisible] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReasonText, setCustomReasonText] = useState("");
  const [savingReason, setSavingReason] = useState(false);

  const fetchArchived = useCallback(() => {
    dispatch(getArchivedPersons({
      type: 1, // Médecins
      page: pagination.currentPage,
      limit: pagination.pageSize,
    }));
  }, [dispatch, pagination]);

  useEffect(() => {
    fetchArchived();
  }, [fetchArchived]);

  const handleRestore = async (id) => {
    const resultAction = await dispatch(restorePerson(id));
    if (restorePerson.fulfilled.match(resultAction)) {
      toast.success('Médecin restauré avec succès !');
      fetchArchived();
    } else {
      toast.error(resultAction.payload || 'La restauration a échoué.');
    }
  };

  // Ouverture de la modale de modification
  const handleOpenEditReason = (record) => {
    setSelectedDoctor(record);
    
    if (record.archiveReason && record.archiveReason.startsWith("Autre:")) {
      setSelectedReason("Autre");
      setCustomReasonText(record.archiveReason.replace("Autre:", "").trim());
    } else if (record.archiveReason) {
      setSelectedReason(record.archiveReason);
      setCustomReasonText("");
    } else {
      setSelectedReason("");
      setCustomReasonText("");
    }
    
    setIsReasonModalVisible(true);
  };

  // ⚡ ENREGISTREMENT DIRECT SANS CRASHER LE REDUX STORE ⚡
  const handleSaveReason = async () => {
    if (!selectedReason) {
      toast.warning("Veuillez sélectionner un motif.");
      return;
    }

    const finalReason = selectedReason === "Autre"
      ? (customReasonText.trim() ? `Autre: ${customReasonText.trim()}` : "Autre motif")
      : selectedReason;

    setSavingReason(true);
    try {
      // Appel API direct pour éviter le crash dans le reducer personsSlice
      await axiosService.patch(`${persons_api_base_url}/${selectedDoctor.id}/archive-reason`, {
        archiveReason: finalReason
      });

      toast.success("Motif d'archivage enregistré !");
      setIsReasonModalVisible(false);
      fetchArchived();
    } catch (e) {
      console.error("Erreur mise à jour motif:", e);
      toast.error(e.response?.data?.message || "Erreur lors de l'enregistrement du motif.");
    } finally {
      setSavingReason(false);
    }
  };

  const columns = [
    { title: 'Nom', dataIndex: 'lastname' },
    { title: 'Prénom', dataIndex: 'firstname' },
    { title: 'Spécialité', dataIndex: ['doctor', 'specialty', 'abreviation'], key: 'specialty' },
    {
      title: "Motif d'archivage",
      dataIndex: 'archiveReason',
      key: 'archiveReason',
      render: (reason, record) => {
        if (reason) {
          return (
            <Space align="center">
              <Tag color="volcano" style={{ fontWeight: 500, margin: 0 }}>
                {reason}
              </Tag>
              <Tooltip title="Modifier le motif">
                <Button 
                  type="text" 
                  size="small" 
                  icon={<EditOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />} 
                  onClick={() => handleOpenEditReason(record)}
                />
              </Tooltip>
            </Space>
          );
        }

        return (
          <Button
            type="dashed"
            size="small"
            icon={<EditOutlined />}
            style={{ color: '#fa8c16', borderColor: '#ffa39e', fontSize: '12px' }}
            onClick={() => handleOpenEditReason(record)}
          >
            + Renseigner le motif
          </Button>
        );
      },
    },
    {
      title: 'Archivé le',
      dataIndex: 'deletedAt',
      render: (date) => date ? new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A',
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <Tooltip title="Restaurer ce médecin">
          <Button
            type="primary"
            icon={<UndoOutlined />}
            onClick={() => handleRestore(record.id)}
          />
        </Tooltip>
      ),
    },
  ];

  const handleTableChange = (page) => {
    setPagination({ currentPage: page.current, pageSize: page.pageSize });
  };

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card bordered={false} className="criclebox tablespace mb-24" title="Médecins Archivés">
            {error && <Alert message={error} type="error" style={{ marginBottom: 16 }} />}
            <Table
              loading={loading}
              columns={columns}
              dataSource={archivedPersons?.data}
              rowKey="id"
              pagination={{ 
                current: pagination.currentPage, 
                pageSize: pagination.pageSize, 
                total: archivedPersons?.total 
              }}
              onChange={handleTableChange}
            />
          </Card>
        </Col>
      </Row>

      {/* MODALE D'ÉDITION DU MOTIF D'ARCHIVAGE */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#1890ff' }} />
            <span>Renseigner le motif d'archivage</span>
          </Space>
        }
        open={isReasonModalVisible}
        onCancel={() => setIsReasonModalVisible(false)}
        onOk={handleSaveReason}
        okText="Enregistrer"
        confirmLoading={savingReason}
        cancelText="Annuler"
        destroyOnClose
      >
        {selectedDoctor && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">Médecin : </Text>
            <Text strong>{selectedDoctor.firstname} {selectedDoctor.lastname}</Text>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>
            Motif de l'archivage <span style={{ color: 'red' }}>*</span> :
          </Text>
          <Select
            placeholder="Sélectionnez une raison..."
            style={{ width: '100%' }}
            value={selectedReason || undefined}
            onChange={(value) => setSelectedReason(value)}
            options={[
              { value: "Cessation d'activité / Retraite", label: "Cessation d'activité / Retraite" },
              { value: "Changement d'adresse / Hors secteur", label: "Changement d'adresse / Hors secteur" },
              { value: "Refus de visite / Non réceptif", label: "Refus de visite / Non réceptif" },
              { value: "Doublon / Erreur de saisie", label: "Doublon / Erreur de saisie" },
              { value: "Décès", label: "Décès" },
              { value: "Autre", label: "Autre motif" },
            ]}
          />
        </div>

        {selectedReason === "Autre" && (
          <div>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Précisez le motif :</Text>
            <Input.TextArea
              rows={2}
              placeholder="Indiquez les détails..."
              value={customReasonText}
              onChange={(e) => setCustomReasonText(e.target.value)}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ArchivedDoctorsPage;